/*!
   Copyright 2019 Ron Buckton

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/
import { R } from 'retemplate';
import { isInt, compareBigInts, normalizeString, OFFSETDATETIME_HASH_SEED } from './internal/core';
import { getDaysInMonth, getDate, getDatePart, getCalendarDateFromWeekDate, getCalendarDateFromOrdinalDate, DatePart } from './internal/date';
import { getTime, getTimePart, TimePart, NANOSECONDS_PER_DAY } from './internal/time';
import { adjustInstant, getInstant, getTimeOfInstant, getDateOfInstant } from './internal/instant';
import { DATE, TIME, OFFSET } from './internal/symbols';
import { EPOCH_DAYS, NANOS_OF_DAY } from './internal/types';
import { OFFSET_DATETIME_EXTENDED, OFFSET_DATETIME_BASIC } from './internal/patterns';
import { Temporal, Components, TimeDelta, TimeUnit, DateDelta } from './types';
import { Zone, ZoneOffset } from './Zone';
import { Clock } from './Clock';
import { CivilDate } from './CivilDate';
import { CivilTime } from './CivilTime';
import { CivilDateTime } from './CivilDateTime';
import { OffsetTime } from './OffsetTime';
import { ZonedDateTime } from './ZonedDateTime';
import { Instant } from './Instant';
import { Duration } from './Duration';
import { Equatable, Comparable, Equaler } from '@esfx/equatable';

const offsetDateTimeExtendedRegExp = new RegExp(R`^${OFFSET_DATETIME_EXTENDED}$`);
const offsetDateTimeBasicRegExp = new RegExp(R`^${OFFSET_DATETIME_BASIC}$`);

export class OffsetDateTime implements Temporal<OffsetDateTime>, Equatable, Comparable {
    /* @internal */ private [DATE]: EPOCH_DAYS;
    /* @internal */ private [TIME]: NANOS_OF_DAY;
    /* @internal */ private [OFFSET]: ZoneOffset;

    constructor(offset: ZoneOffset, year: number, month: number, day: number, hour: number, minute: number, second: number = 0, millisecond: number = 0, nanosecond: number = 0) {
        if (!isInt(year)) throw new RangeError(`The value ${year} is not a valid year.`);
        if (!isInt(month) || month < 1 || month > 12) throw new RangeError(`The value ${month} is not a valid month.`);
        if (!isInt(day) || day < 1 || day > getDaysInMonth(year, month)) throw new RangeError(`The value ${day} is not a valid day.`);
        if (!isInt(hour) || hour < 0 || hour > 24) throw new RangeError(`The value ${hour} is not a valid hour.`);
        if (!isInt(minute) || minute < 0 || minute > 59) throw new RangeError(`The value ${minute} is not a valid minute.`);
        if (!isInt(second) || second < 0 || second > 59) throw new RangeError(`The value ${second} is not a valid second.`);
        if (!isInt(millisecond) || millisecond < 0 || millisecond > 1e3) throw new RangeError(`The value ${millisecond} is not a valid millisecond.`);
        if (!isInt(nanosecond) || nanosecond < 0 || nanosecond > 1e6) throw new RangeError(`The value ${nanosecond} is not a valid nanosecond.`);
        if (hour === 24 && minute !== 0) throw new RangeError(`The value ${minute} is not a valid minute of hour ${hour}.`);
        if (hour === 24 && second !== 0) throw new RangeError(`The value ${second} is not a valid second of hour ${hour}.`);
        if (hour === 24 && millisecond !== 0) throw new RangeError(`The value ${millisecond} is not a valid second of hour ${millisecond}.`);
        if (hour === 24 && nanosecond !== 0) throw new RangeError(`The value ${nanosecond} is not a valid second of hour ${nanosecond}.`);
        this[DATE] = getDate(year, month, day);
        this[TIME] = getTime(hour, minute, second, millisecond, nanosecond);
        this[OFFSET] = offset;
    }

    get offset() { return this[OFFSET]; }
    get offsetSeconds() { return this.offset.offsetSeconds; }
    get offsetString() { return this.offset.offsetString; }
    get year() { return getDatePart(this[DATE], DatePart.year); }
    get month() { return getDatePart(this[DATE], DatePart.month); }
    get day() { return getDatePart(this[DATE], DatePart.day); }
    get dayOfYear() { return getDatePart(this[DATE], DatePart.dayOfYear); }
    get dayOfWeek() { return getDatePart(this[DATE], DatePart.dayOfWeek); }
    get weekOfYear() { return getDatePart(this[DATE], DatePart.weekOfYear); }
    get hour() { return this[TIME] === NANOSECONDS_PER_DAY ? 24 : getTimePart(this[TIME], TimePart.hour); }
    get minute() { return getTimePart(this[TIME], TimePart.minute); }
    get second() { return getTimePart(this[TIME], TimePart.second); }
    get millisecond() { return getTimePart(this[TIME], TimePart.millisecond); }
    get nanosecond() { return getTimePart(this[TIME], TimePart.nanosecond); }

    plus(deltas?: Components<DateDelta | TimeDelta>) {
        return this
            .toCivilDateTime()
            .plus(deltas)
            .withOffset(this.offset);
    }

    minus(deltas?: Components<DateDelta | TimeDelta>) {
        return this
            .toCivilDateTime()
            .minus(deltas)
            .withOffset(this.offset);
    }

    with(components?: Components<DateDelta | TimeUnit>) {
        return this
            .toCivilDateTime()
            .with(components)
            .withOffset(this.offset);
    }

    withOffsetSameInstant(offset: ZoneOffset) {
        return this
            .toZonedDateTime()
            .withZoneSameInstant(offset)
            .toOffsetDateTime();
    }

    withOffsetSameLocal(offset: ZoneOffset) {
        return this
            .toZonedDateTime()
            .withZoneSameLocal(offset)
            .toOffsetDateTime();
    }

    withZoneSameInstant(zone: Zone) {
        return this.toZonedDateTime().withZoneSameInstant(zone);
    }

    withZoneSimilarLocal(zone: Zone) {
        return this.toZonedDateTime().withZoneSameLocal(zone);
    }

    until(other: OffsetDateTime): Duration {
        if (!(other instanceof OffsetDateTime)) throw new TypeError("OffsetTime expected: other");
        return this
            .toCivilDateTime()
            .until(other
                .withOffsetSameInstant(this.offset)
                .toCivilDateTime());
    }

    since(other: OffsetDateTime): Duration {
        if (!(other instanceof OffsetDateTime)) throw new TypeError("OffsetTime expected: other");
        return other.until(this);
    }

    toCivilDate() {
        return new CivilDate(
            this.year,
            this.month,
            this.day);
    }

    toCivilTime() {
        return new CivilTime(
            this.hour,
            this.minute,
            this.second,
            this.millisecond,
            this.nanosecond);
    }

    toCivilDateTime() {
        return new CivilDateTime(
            this.year,
            this.month,
            this.day,
            this.hour,
            this.minute,
            this.second,
            this.millisecond,
            this.nanosecond);
    }

    toOffsetTime() {
        return new OffsetTime(
            this.offset,
            this.hour,
            this.minute,
            this.second,
            this.millisecond,
            this.nanosecond);
    }

    toZonedDateTime() {
        return new ZonedDateTime(this.toInstant(), this.offset);
    }

    toInstant() {
        return new Instant(adjustInstant(getInstant(this[DATE], this[TIME]), -this.offsetSeconds));
    }

    equals(other: OffsetDateTime) {
        return other !== null
            && other !== undefined
            && this[DATE] === other[DATE]
            && this[TIME] === other[TIME]
            && this[OFFSET].equals(other[OFFSET]);
    }

    compareTo(other: OffsetDateTime): number {
        return compareBigInts(
            adjustInstant(getInstant(this[DATE], this[TIME]), -this.offsetSeconds),
            adjustInstant(getInstant(other[DATE], other[TIME]), -other.offsetSeconds));
    }

    toString() {
        return `${this.toCivilDateTime()}${this.offsetString}`;
    }

    toISOString() {
        return this.toString();
    }

    toJSON() {
        return this.toString();
    }

    static from(offset: ZoneOffset, datetime: CivilDateTime) {
        return OffsetDateTime.fromEpochDays(offset, datetime[DATE], datetime[TIME]);
    }

    static fromEpochDays(offset: ZoneOffset, days: number, nanoseconds = 0n): OffsetDateTime {
        if (!isInt(days)) throw new RangeError(`The value ${days} is not valid for the number of days.`);
        if (nanoseconds < 0 || nanoseconds >= NANOSECONDS_PER_DAY) throw new RangeError(`The value ${nanoseconds} is not valid for the number of nanoseconds since the start of the day.`);
        return Object.create(OffsetDateTime.prototype, {
            [OFFSET]: { value: offset },
            [DATE]: { value: days },
            [TIME]: { value: nanoseconds }
        });
    }

    static fromCalendarDateTime(offset: ZoneOffset, year: number, month: number, day: number, hour: number, minute: number, second = 0, millisecond = 0, nanosecond = 0) {
        return new OffsetDateTime(
            offset,
            year,
            month,
            day,
            hour,
            minute,
            second,
            millisecond,
            nanosecond);
    }

    static fromWeekDateTime(offset: ZoneOffset, year: number, weekOfYear: number, dayOfWeek: number, hour: number, minute: number, second = 0, millisecond = 0, nanosecond = 0) {
        const calendar = getCalendarDateFromWeekDate(year, weekOfYear, dayOfWeek);
        return OffsetDateTime.fromCalendarDateTime(
            offset,
            calendar.year,
            calendar.month,
            calendar.day,
            hour,
            minute,
            second,
            millisecond,
            nanosecond);

    }

    static fromOrdinalDateTime(offset: ZoneOffset, year: number, dayOfYear: number, hour: number, minute: number, second = 0, millisecond = 0, nanosecond = 0) {
        const calendar = getCalendarDateFromOrdinalDate(year, dayOfYear);
        return OffsetDateTime.fromCalendarDateTime(
            offset,
            calendar.year,
            calendar.month,
            calendar.day,
            hour,
            minute,
            second,
            millisecond,
            nanosecond);
    }

    static fromInstant(offset: ZoneOffset, instant: Instant) {
        let ticks = getInstant(0n, getTime(0, 0, 0, instant.milliseconds, instant.nanoseconds));
        ticks = adjustInstant(ticks, offset.offsetSeconds);
        return fromDateTime(
            offset,
            getDateOfInstant(ticks),
            getTimeOfInstant(ticks));
    }

    static fromString(text: string) {
        const normalText = normalizeString(text);
        const match = offsetDateTimeExtendedRegExp.exec(normalText) || offsetDateTimeBasicRegExp.exec(normalText);
        if (match) {
            const { year, month, day, weekOfYear, dayOfWeek, dayOfYear, hour, minute, second, millisecond = "", nanosecond = "", offset } = match.groups!;
            const zone = ZoneOffset.fromString(offset);
            if (year && month && day) return new OffsetDateTime(zone, +year, +month, +day, +hour, +minute, +second, +millisecond.padEnd(3, "0"), +nanosecond.padEnd(6, "0"));
            if (year && weekOfYear && dayOfWeek) return OffsetDateTime.fromWeekDateTime(zone, +year, +weekOfYear, +dayOfWeek, +hour, +minute, +second, +millisecond.padEnd(3, "0"), +nanosecond.padEnd(6, "0"));
            if (year && dayOfYear) return OffsetDateTime.fromOrdinalDateTime(zone, +year, +dayOfYear, +hour, +minute, +second, +millisecond.padEnd(3, "0"), +nanosecond.padEnd(6, "0"));
        }

        throw new Error(`Invalid offset date-time string '${text}'.`);
    }

    static now(clock: Clock) {
        const instant = clock.currentInstant();
        const offset = clock.zone.getOffset(instant);
        return OffsetDateTime.fromInstant(offset, instant);
    }

    [Equatable.equals](other: unknown) {
        return other instanceof OffsetDateTime
            && this.equals(other);
    }

    [Equatable.hash]() {
        let hc = OFFSETDATETIME_HASH_SEED;
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this[DATE]);
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this[TIME]);
        hc = ((hc << 7) | (hc >>> 25)) ^ this[OFFSET][Equatable.hash]();
        return hc;
    }

    [Comparable.compareTo](other: unknown) {
        if (!(other instanceof OffsetDateTime)) throw new TypeError();
        return this.compareTo(other);
    }
}

Object.defineProperty(OffsetDateTime.prototype, Symbol.toStringTag, {
    writable: true,
    configurable: true,
    value: "OffsetDateTime"
});

function fromDateTime(zone: ZoneOffset, date: EPOCH_DAYS, time: NANOS_OF_DAY): OffsetDateTime {
    return Object.create(OffsetDateTime.prototype, {
        [OFFSET]: { value: zone },
        [DATE]: { value: date },
        [TIME]: { value: time }
    });
}